import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import JobDetail from '../components/JobDetail';
import StructuredData from '../components/StructuredData';
import { getJob } from '@/api';
import type { LegacyJob, LegacyCity } from '../types';
import { trackJobViewed, trackJobCityClicked } from '../src/lib/analytics';
import { useCities } from '../src/contexts/CitiesContext';
import type { CityFromAPI } from '../src/api/cities';

// CityFromAPI → LegacyCity 适配
const toLegacyCity = (c: CityFromAPI): LegacyCity => ({
  id: c.id,
  name: c.nameZh || c.name,
  enName: c.nameEn || c.enName,
  country: c.country?.nameEn || c.countryCode,
  image: c.image,
  jobCount: c.jobCount,
  isRemoteOnly: c.isRemoteOnly,
});

// 使用 LegacyJob 类型用于向后兼容
type Job = LegacyJob;

const JobDetailPage: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { citiesMap } = useCities();

  // 从 API 获取职位详情
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchJob = async () => {
      if (!jobId) {
        setError('Job ID is required');
        setLoading(false);
        return;
      }
      try {
        const jobData = await getJob(jobId);
        setJob(jobData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load job');
      } finally {
        setLoading(false);
      }
    };
    fetchJob();
  }, [jobId]);

  // 查找职位对应的城市
  const cityFromApi = job ? citiesMap.get(job.city) || null : null;
  const city = cityFromApi ? toLegacyCity(cityFromApi) : null;

  // 判断来源页面
  const getSourceView = (): 'home' | 'all-jobs' | 'offices' | 'life' => {
    const from = location.state?.from;
    if (from === 'jobs') return 'all-jobs';
    if (from === 'offices') return 'offices';
    if (from === 'city') return 'home';
    return 'all-jobs';
  };

  // 更新页面标题和元标签
  useEffect(() => {
    if (job) {
      // 更新页面标题
      document.title = `${job.title} | UHomes Careers`;

      // 更新或创建 meta description
      const description = job.description && job.description.length > 0
        ? `${job.description[0].slice(0, 150)}...`
        : `${t('html.title')} - ${job.title}`;

      updateMetaTag('name', 'description', description);
      updateMetaTag('property', 'og:title', job.title);
      updateMetaTag('property', 'og:description', description);
      updateMetaTag('property', 'og:type', 'website');
      updateMetaTag('property', 'og:url', `https://careers.uhomes.com/jobs/${job.id}`);
      updateMetaTag('property', 'og:site_name', 'UHomes Careers');

      // 更新 canonical URL
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', `https://careers.uhomes.com/jobs/${job.id}`);

      // 更新 hreflang 标签
      const updateHreflang = (lang: string, url: string) => {
        let hreflang = document.querySelector(`link[hreflang="${lang}"]`);
        if (!hreflang) {
          hreflang = document.createElement('link');
          hreflang.setAttribute('hreflang', lang);
          document.head.appendChild(hreflang);
        }
        hreflang.setAttribute('href', url);
      };

      updateHreflang('zh', `https://careers.uhomes.com/jobs/${job.id}`);
      updateHreflang('en', `https://careers.uhomes.com/jobs/${job.id}`);
      updateHreflang('x-default', `https://careers.uhomes.com/jobs/${job.id}`);

      trackJobViewed({
        id: job.id,
        title: job.title,
        department: job.department,
        city: job.city,
        type: job.type,
        workStyle: job.workStyle,
      });
    }
  }, [job, t]);

  // 辅助函数：更新或创建 meta 标签
  const updateMetaTag = (attrName: string, attrValue: string, content: string) => {
    let meta = document.querySelector(`meta[${attrName}="${attrValue}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute(attrName, attrValue);
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', content);
  };

  // 加载状态
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // 错误状态
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 pt-16">
        <div className="max-w-4xl mx-auto px-4 py-16 sm:py-24 text-center">
          <h1 className="text-2xl sm:text-3xl font-black text-slate-900 mb-4">
            {t('notFound.errorOccurred', '加载失败')}
          </h1>
          <p className="text-slate-500 text-base sm:text-lg mb-12 max-w-md mx-auto leading-relaxed">
            {error}
          </p>
          <button
            onClick={() => navigate('/jobs')}
            className="inline-flex items-center justify-center gap-2 bg-slate-900 text-white px-8 py-4 rounded-xl font-bold text-base hover:bg-blue-600 transition-all shadow-lg shadow-slate-300 active:scale-95 cursor-pointer"
          >
            {t('notFound.viewAllJobs', '查看所有职位')}
          </button>
        </div>
      </div>
    );
  }

  // 职位不存在，显示友好的 404 状态
  if (!job && !loading) {
    return (
      <div className="min-h-screen bg-slate-50 pt-16">
        <div className="max-w-4xl mx-auto px-4 py-16 sm:py-24 text-center">
          
          {/* 大号 404 数字 */}
          <div className="mb-8">
            <svg 
              viewBox="0 0 400 160" 
              className="w-full max-w-md mx-auto h-auto"
              aria-hidden="true"
            >
              <text 
                x="200" 
                y="110" 
                textAnchor="middle" 
                className="fill-slate-300" 
                style={{ fontSize: '140px', fontWeight: 900, fontFamily: 'system-ui, -apple-system, sans-serif' }}
              >
                4
                <tspan className="fill-blue-500">0</tspan>
                4
              </text>
            </svg>
          </div>

          {/* 标题 */}
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900 mb-4">
            {t('notFound.jobNotFound', '职位不存在')}
          </h1>

          {/* 描述 */}
          <p className="text-slate-500 text-base sm:text-lg mb-12 max-w-md mx-auto leading-relaxed">
            {t('notFound.jobDescription', '该职位可能已下线或链接有误，请浏览其他热门职位')}
          </p>

          {/* 按钮组 */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/jobs')}
              className="inline-flex items-center justify-center gap-2 bg-slate-900 text-white px-8 py-4 rounded-xl font-bold text-base hover:bg-blue-600 transition-all shadow-lg shadow-slate-300 active:scale-95 cursor-pointer"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {t('notFound.viewAllJobs', '查看所有职位')}
            </button>
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center justify-center gap-2 bg-white text-slate-900 px-8 py-4 rounded-xl font-bold text-base border-2 border-slate-200 hover:border-blue-500 hover:text-blue-600 transition-all active:scale-95 cursor-pointer"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              {t('notFound.backHome', '返回首页')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleBack = () => {
    // 根据来源返回
    const from = location.state?.from;
    if (from === 'city' && city) {
      navigate(`/cities/${city.id}`);
    } else {
      navigate('/jobs');
    }
  };

  const handleCityClick = () => {
    if (city) {
      trackJobCityClicked(job.id, city.id);
      navigate(`/cities/${city.id}`);
    }
  };

  // 如果 job 为 null，不渲染 JobDetail
  if (!job) {
    return null;
  }

  return (
    <>
      <StructuredData type="jobPosting" job={job} />
      <JobDetail
        job={job}
        onBack={handleBack}
        sourceView={getSourceView()}
        selectedCity={city}
        onCityClick={handleCityClick}
      />
    </>
  );
};

export default JobDetailPage;
